import React, { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";
import { hideModal, showModal } from "../../utils/modalUtility";
import config from "../../config";

const PartnerApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedApp, setSelectedApp] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const axiosAPI = useAxios();

  // Edit State
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: "",
    restaurant_name: "",
    owner_name: "",
    owner_phone: "",
    owner_email: "",
    city: "",
    pincode: "",
    complete_address: "",
    landmark: "",
    service_type: "both",
    veg_type: "pureveg",
    cost_for_two: "",
    open_time: "",
    close_time: "",
    delivery_radius_km: "",
    min_order_amount: "",
    fssai_no: "",
    fssai_expiry: "",
    pan_no: "",
    pan_name: "",
    gst_no: "",
    bank_name: "",
    account_holder_name: "",
    bank_account_no: "",
    ifsc_code: "",
    status: "draft",
  });

  const handleEdit = async (app) => {
    setEditLoading(true);
    // Pre-fill initial known fields
    setEditFormData({
      id: app.id,
      restaurant_name: app.restaurant_name || "",
      owner_name: app.owner_name || app.user_name || "",
      owner_phone: app.owner_phone || app.mobile_no || app.user_phone || "",
      owner_email: app.owner_email || app.user_email || "",
      city: app.city || app.restaurant_city || "",
      pincode: app.pincode || "",
      complete_address: app.complete_address || "",
      landmark: app.landmark || "",
      service_type: app.service_type || "both",
      veg_type: app.veg_type || "pureveg",
      cost_for_two: app.cost_for_two || "",
      open_time: app.open_time || "",
      close_time: app.close_time || "",
      delivery_radius_km: app.delivery_radius_km || "",
      min_order_amount: app.min_order_amount || "",
      fssai_no: app.fssai_no || "",
      fssai_expiry: app.fssai_expiry ? app.fssai_expiry.split("T")[0] : "",
      pan_no: app.pan_no || "",
      pan_name: app.pan_name || "",
      gst_no: app.gst_no || "",
      bank_name: app.bank_name || "",
      account_holder_name: app.account_holder_name || "",
      bank_account_no: app.bank_account_no || "",
      ifsc_code: app.ifsc_code || "",
      status: app.status || "draft",
    });
    showModal("editPartnerAppModal");

    // Fetch full application details
    try {
      const response = await axiosAPI.get(`/api/partner-applications/${app.id}`);
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        setEditFormData({
          id: d.id,
          restaurant_name: d.restaurant_name || "",
          owner_name: d.owner_name || d.user_name || "",
          owner_phone: d.owner_phone || d.mobile_no || d.user_phone || "",
          owner_email: d.owner_email || d.user_email || "",
          city: d.city || d.restaurant_city || "",
          pincode: d.pincode || "",
          complete_address: d.complete_address || "",
          landmark: d.landmark || "",
          service_type: d.service_type || "both",
          veg_type: d.veg_type || "pureveg",
          cost_for_two: d.cost_for_two || "",
          open_time: d.open_time || "",
          close_time: d.close_time || "",
          delivery_radius_km: d.delivery_radius_km || "",
          min_order_amount: d.min_order_amount || "",
          fssai_no: d.fssai_no || "",
          fssai_expiry: d.fssai_expiry ? d.fssai_expiry.split("T")[0] : "",
          pan_no: d.pan_no || "",
          pan_name: d.pan_name || "",
          gst_no: d.gst_no || "",
          bank_name: d.bank_name || "",
          account_holder_name: d.account_holder_name || "",
          bank_account_no: d.bank_account_no || "",
          ifsc_code: d.ifsc_code || "",
          status: d.status || "draft",
        });
      }
    } catch (error) {
      console.error("Error fetching application details for edit:", error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      const response = await axiosAPI.put(
        `/api/partner-applications/${editFormData.id}`,
        editFormData,
      );
      if (response.data.success) {
        if (window.notifyMessage) {
          window.notifyMessage("Success", "Application updated successfully");
        } else {
          alert("Application updated successfully");
        }
        hideModal("editPartnerAppModal");
        fetchApplications();
        if (selectedApp && selectedApp.id === editFormData.id) {
          setSelectedApp((prev) => ({ ...prev, ...editFormData }));
        }
      }
    } catch (error) {
      console.error("Error updating application:", error);
      alert(error.response?.data?.message || "Failed to update application");
    } finally {
      setEditSubmitting(false);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await axiosAPI.get(
        `/api/partner-applications?status=${statusFilter}`,
      );
      if (response.data.success) {
        setApplications(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching partner applications:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const handleView = async (id) => {
    setModalLoading(true);
    showModal("partnerAppModal");
    try {
      const response = await axiosAPI.get(`/api/partner-applications/${id}`);
      if (response.data.success) {
        setSelectedApp(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching application details:", error);
    }
    setModalLoading(false);
  };

  const handleStatusDropdownChange = async (id, newStatus) => {
    if (
      !window.confirm(
        `Are you sure you want to change the status to ${newStatus}?`,
      )
    )
      return;
    try {
      const response = await axiosAPI.put(
        `/api/partner-applications/${id}/status`,
        { status: newStatus },
      );
      if (response.data.success) {
        alert(response.data.message);
        fetchApplications();
        // If modal is open for this app, update its state too
        if (selectedApp && selectedApp.id === id) {
          setSelectedApp((prev) => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  const updateStatus = async (id, newStatus) => {
    if (
      !window.confirm(
        `Are you sure you want to change this application status to ${newStatus}?`,
      )
    )
      return;

    try {
      const response = await axiosAPI.put(
        `/api/partner-applications/${id}/status`,
        { status: newStatus },
      );
      if (response.data.success) {
        alert(response.data.message);
        fetchApplications();
        if (selectedApp && selectedApp.id === id) {
          setSelectedApp((prev) => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  const getFullUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base = config.apiBaseUrl || "http://localhost:5000";
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const parseArrayOrList = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string")
        return parsed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [parsed];
    } catch {
      if (typeof val === "string") {
        return val
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [val];
    }
  };

  const renderFilePreview = (url, label) => {
    if (!url)
      return (
        <span className="badge bg-light text-muted border px-2 py-1">
          Not Uploaded
        </span>
      );
    const fileUrl = getFullUrl(url);
    const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url);

    if (isImage) {
      return (
        <div className="d-inline-flex flex-column align-items-start gap-1 mb-2 me-3">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={`Click to view full ${label}`}
          >
            <img
              src={fileUrl}
              alt={label}
              style={{
                width: "90px",
                height: "70px",
                objectFit: "cover",
                borderRadius: "6px",
                border: "1px solid #dee2e6",
              }}
              onError={(e) => {
                // Fallback to website port 5001 if backend didn't serve it
                if (
                  !e.target.dataset.triedFallback &&
                  !fileUrl.startsWith("http://localhost:5001")
                ) {
                  e.target.dataset.triedFallback = "true";
                  e.target.src = `http://localhost:5001${url.startsWith("/") ? "" : "/"}${url}`;
                }
              }}
            />
          </a>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-xs btn-outline-primary py-0 px-2"
            style={{ fontSize: "11px" }}
          >
            <i className="fa-solid fa-eye me-1"></i>View {label}
          </a>
        </div>
      );
    }

    return (
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm btn-outline-primary mb-2 me-2"
      >
        <i className="fa-solid fa-file-pdf me-1 text-danger"></i> View {label}
      </a>
    );
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approve" || s === "approved") {
      return <span className="badge bg-success">APPROVED</span>;
    }
    if (s === "rejected") {
      return <span className="badge bg-danger">REJECTED</span>;
    }
    if (s === "pending") {
      return <span className="badge bg-warning text-dark">PENDING</span>;
    }
    return (
      <span className="badge bg-secondary">
        {s ? s.toUpperCase() : "DRAFT"}
      </span>
    );
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="page-header d-flex justify-content-between align-items-center">
          <h4 className="page-title">Partner Applications</h4>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">
                  All Applications ({applications?.length || 0})
                </h4>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Filter:</span>
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ width: "140px" }}
                  >
                    <option value="all">All Applications</option>
                    <option value="pending">Pending</option>
                    <option value="approve">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Sr no</th>
                        <th>Restaurant Name</th>
                        <th>City</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Applied On</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div
                              className="spinner-border spinner-border-sm text-primary me-2"
                              role="status"
                            ></div>
                            Loading applications...
                          </td>
                        </tr>
                      ) : applications.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center py-4 text-muted"
                          >
                            No applications found.
                          </td>
                        </tr>
                      ) : (
                        applications.map((app, index) => (
                          <tr key={app.id}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{app.restaurant_name || "N/A"}</strong>
                            </td>
                            <td>{app.city || app.restaurant_city || "-"}</td>
                            <td>{app.owner_phone || app.mobile_no || "-"}</td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                value={app.status || "draft"}
                                onChange={(e) =>
                                  handleStatusDropdownChange(
                                    app.id,
                                    e.target.value,
                                  )
                                }
                                style={{ width: "120px" }}
                              >
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                                <option value="draft">Draft</option>
                              </select>
                            </td>
                            <td>{formatDate(app.created_at)}</td>
                            <td>
                              <button
                                onClick={() => handleView(app.id)}
                                className="btn btn-icon btn-round btn-primary btn-sm me-2"
                                title="View Full Details"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button
                                onClick={() => handleEdit(app)}
                                className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                title="Edit Application"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Review Modal */}
      <div
        className="modal fade"
        id="partnerAppModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-light border-bottom">
              <div>
                <h5 className="modal-title fw-bold mb-0">
                  Review Application:{" "}
                  {selectedApp ? selectedApp.restaurant_name : ""}
                </h5>
                {selectedApp && (
                  <small className="text-muted">
                    Partner ID #{selectedApp.partner_id} &bull; Applied:{" "}
                    {formatDate(selectedApp.created_at)}
                  </small>
                )}
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => hideModal("partnerAppModal")}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-4">
              {modalLoading ? (
                <div className="text-center py-5">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                  ></div>
                  <div className="mt-2 text-muted">
                    Loading application details...
                  </div>
                </div>
              ) : selectedApp ? (
                <div className="row g-4">
                  {/* 1. Basic & Contact Info */}
                  <div className="col-lg-6">
                    <div className="card h-100 shadow-none border">
                      <div className="card-header bg-white py-2 border-bottom">
                        <h6 className="fw-bold mb-0 text-primary">
                          <i className="fa-solid fa-store me-2"></i>1.
                          Restaurant & Basic Info
                        </h6>
                      </div>
                      <div className="card-body p-0">
                        <table className="table table-sm table-bordered mb-0">
                          <tbody>
                            <tr>
                              <th style={{ width: "35%" }} className="bg-light">
                                Restaurant Name
                              </th>
                              <td>
                                <strong>
                                  {selectedApp.restaurant_name || "N/A"}
                                </strong>
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Owner / Contact</th>
                              <td>
                                {selectedApp.owner_name ||
                                  selectedApp.user_name ||
                                  "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Mobile Number</th>
                              <td>
                                {selectedApp.owner_phone ||
                                  selectedApp.mobile_no ||
                                  selectedApp.user_phone ||
                                  "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Owner Email</th>
                              <td>
                                {selectedApp.owner_email ||
                                  selectedApp.user_email ||
                                  "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">City</th>
                              <td>
                                {selectedApp.city ||
                                  selectedApp.restaurant_city ||
                                  "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Pincode</th>
                              <td>{selectedApp.pincode || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="bg-light">Complete Address</th>
                              <td>{selectedApp.complete_address || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="bg-light">Landmark</th>
                              <td>{selectedApp.landmark || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="bg-light">Coordinates / Map</th>
                              <td>
                                {selectedApp.latitude &&
                                selectedApp.longitude ? (
                                  <a
                                    href={`https://maps.google.com/?q=${selectedApp.latitude},${selectedApp.longitude}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary text-decoration-none"
                                  >
                                    <i className="fa-solid fa-location-dot me-1 text-danger"></i>
                                    {selectedApp.latitude},{" "}
                                    {selectedApp.longitude} (Open Map)
                                  </a>
                                ) : (
                                  selectedApp.map_location || "N/A"
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Current Status</th>
                              <td>{getStatusBadge(selectedApp.status)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* 2. Operations & Menu Settings */}
                  <div className="col-lg-6">
                    <div className="card h-100 shadow-none border">
                      <div className="card-header bg-white py-2 border-bottom">
                        <h6 className="fw-bold mb-0 text-primary">
                          <i className="fa-solid fa-clock me-2"></i>2.
                          Operations & Setup
                        </h6>
                      </div>
                      <div className="card-body p-0">
                        <table className="table table-sm table-bordered mb-0">
                          <tbody>
                            <tr>
                              <th style={{ width: "35%" }} className="bg-light">
                                Service Type
                              </th>
                              <td className="text-capitalize">
                                {selectedApp.service_type ||
                                  "Both (Dine-in & Delivery)"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Veg / Food Type</th>
                              <td className="text-capitalize">
                                <span className="badge bg-success me-1">
                                  <i className="fa-solid fa-leaf me-1"></i>
                                  {selectedApp.veg_type || "Pure Veg"}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Cuisines</th>
                              <td>
                                {parseArrayOrList(selectedApp.cuisines).length >
                                0 ? (
                                  parseArrayOrList(selectedApp.cuisines).map(
                                    (c, i) => (
                                      <span
                                        key={i}
                                        className="badge bg-light text-dark border me-1 mb-1"
                                      >
                                        {c}
                                      </span>
                                    ),
                                  )
                                ) : (
                                  <span className="text-muted">N/A</span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Operating Hours</th>
                              <td>
                                {selectedApp.open_time || selectedApp.close_time
                                  ? `${selectedApp.open_time || "-"} to ${selectedApp.close_time || "-"}`
                                  : "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Approx Cost for Two</th>
                              <td>
                                {selectedApp.cost_for_two
                                  ? `₹${selectedApp.cost_for_two}`
                                  : "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Delivery Radius</th>
                              <td>
                                {selectedApp.delivery_radius_km
                                  ? `${selectedApp.delivery_radius_km} km`
                                  : "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Min Order Amount</th>
                              <td>
                                {selectedApp.min_order_amount
                                  ? `₹${selectedApp.min_order_amount}`
                                  : "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Best Selling Dishes</th>
                              <td>
                                {parseArrayOrList(selectedApp.top_dishes)
                                  .length > 0 ? (
                                  parseArrayOrList(selectedApp.top_dishes).map(
                                    (d, i) => (
                                      <span
                                        key={i}
                                        className="badge bg-light text-dark border me-1 mb-1"
                                      >
                                        {d}
                                      </span>
                                    ),
                                  )
                                ) : (
                                  <span className="text-muted">N/A</span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* 3. Legal & Verification Documents */}
                  <div className="col-lg-6">
                    <div className="card h-100 shadow-none border">
                      <div className="card-header bg-white py-2 border-bottom">
                        <h6 className="fw-bold mb-0 text-primary">
                          <i className="fa-solid fa-file-shield me-2"></i>3.
                          Legal Documents
                        </h6>
                      </div>
                      <div className="card-body p-0">
                        <table className="table table-sm table-bordered mb-0">
                          <tbody>
                            <tr>
                              <th style={{ width: "35%" }} className="bg-light">
                                FSSAI License
                              </th>
                              <td>
                                <div>
                                  <strong>Number:</strong>{" "}
                                  {selectedApp.fssai_no || "N/A"}
                                </div>
                                <div>
                                  <strong>Expiry:</strong>{" "}
                                  {formatDate(selectedApp.fssai_expiry)}
                                </div>
                                <div className="mt-1">
                                  {renderFilePreview(
                                    selectedApp.fssai_doc_url,
                                    "FSSAI Document",
                                  )}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">PAN Card</th>
                              <td>
                                <div>
                                  <strong>Number:</strong>{" "}
                                  {selectedApp.pan_no || "N/A"}
                                </div>
                                <div>
                                  <strong>Name on PAN:</strong>{" "}
                                  {selectedApp.pan_name || "N/A"}
                                </div>
                                <div className="mt-1">
                                  {renderFilePreview(
                                    selectedApp.pan_doc_url,
                                    "PAN Document",
                                  )}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">GSTIN</th>
                              <td>
                                <div>
                                  <strong>Number:</strong>{" "}
                                  {selectedApp.gst_no || "N/A"}
                                </div>
                                <div className="mt-1">
                                  {renderFilePreview(
                                    selectedApp.gst_doc_url,
                                    "GST Document",
                                  )}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* 4. Bank Account Details */}
                  <div className="col-lg-6">
                    <div className="card h-100 shadow-none border">
                      <div className="card-header bg-white py-2 border-bottom">
                        <h6 className="fw-bold mb-0 text-primary">
                          <i className="fa-solid fa-building-columns me-2"></i>
                          4. Bank Account Details
                        </h6>
                      </div>
                      <div className="card-body p-0">
                        <table className="table table-sm table-bordered mb-0">
                          <tbody>
                            <tr>
                              <th style={{ width: "35%" }} className="bg-light">
                                Bank Name
                              </th>
                              <td>{selectedApp.bank_name || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="bg-light">Account Holder</th>
                              <td>
                                {selectedApp.account_holder_name || "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Account Number</th>
                              <td>
                                <code>
                                  {selectedApp.bank_account_no || "N/A"}
                                </code>
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">IFSC Code</th>
                              <td>
                                <code>{selectedApp.ifsc_code || "N/A"}</code>
                              </td>
                            </tr>
                            <tr>
                              <th className="bg-light">Cheque / Passbook</th>
                              <td>
                                {renderFilePreview(
                                  selectedApp.cheque_doc_url,
                                  "Bank Document",
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* 5. Outlet & Menu Photos */}
                  <div className="col-12">
                    <div className="card shadow-none border">
                      <div className="card-header bg-white py-2 border-bottom">
                        <h6 className="fw-bold mb-0 text-primary">
                          <i className="fa-solid fa-images me-2"></i>5. Outlet &
                          Menu Photos
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-4">
                            <div className="border rounded p-2 text-center bg-light">
                              <small className="fw-bold d-block mb-2 text-muted">
                                Storefront Photo
                              </small>
                              {renderFilePreview(
                                selectedApp.storefront_photo,
                                "Storefront",
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="border rounded p-2 text-center bg-light">
                              <small className="fw-bold d-block mb-2 text-muted">
                                Kitchen Photo
                              </small>
                              {renderFilePreview(
                                selectedApp.kitchen_photo,
                                "Kitchen",
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="border rounded p-2 text-center bg-light">
                              <small className="fw-bold d-block mb-2 text-muted">
                                Signature / Top Dish
                              </small>
                              {renderFilePreview(
                                selectedApp.top_dish_photo,
                                "Top Dish",
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-top">
                          <h6 className="fw-bold small text-muted mb-2">
                            Menu Pages & Card Photos:
                          </h6>
                          <div className="d-flex flex-wrap gap-2">
                            {parseArrayOrList(selectedApp.menu_photos).length >
                            0 ? (
                              parseArrayOrList(selectedApp.menu_photos).map(
                                (url, i) => (
                                  <div
                                    key={i}
                                    className="border rounded p-2 text-center bg-light"
                                  >
                                    <small className="fw-bold d-block mb-1 text-muted">
                                      Page {i + 1}
                                    </small>
                                    {renderFilePreview(
                                      url,
                                      `Menu Page ${i + 1}`,
                                    )}
                                  </div>
                                ),
                              )
                            ) : (
                              <span className="badge bg-light text-muted border px-2 py-1">
                                No Menu Photos Uploaded
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  Failed to load details.
                </div>
              )}
            </div>
            <div className="modal-footer bg-light">
              <div className="me-auto">
                {selectedApp && (
                  <span className="text-muted small">
                    Current Status: {getStatusBadge(selectedApp.status)}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => hideModal("partnerAppModal")}
              >
                Close
              </button>
              {selectedApp && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    onClick={() => {
                      hideModal("partnerAppModal");
                      handleEdit(selectedApp);
                    }}
                  >
                    <i className="fas fa-edit me-1"></i> Edit Details
                  </button>
                  {selectedApp.status !== "approve" && selectedApp.status !== "approved" && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => updateStatus(selectedApp.id, "approve")}
                    >
                      <i className="fa-solid fa-check me-1"></i> Approve
                      Application
                    </button>
                  )}
                  {selectedApp.status !== "rejected" && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => updateStatus(selectedApp.id, "rejected")}
                    >
                      <i className="fa-solid fa-xmark me-1"></i> Reject
                    </button>
                  )}
                  {selectedApp.status !== "pending" && (
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={() => updateStatus(selectedApp.id, "pending")}
                    >
                      <i className="fa-solid fa-clock me-1"></i> Set Pending
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Partner Application Modal */}
      <div
        className="modal fade"
        id="editPartnerAppModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <form
            onSubmit={handleEditSubmit}
            className="modal-content d-flex flex-column"
            style={{ maxHeight: "90vh" }}
          >
            <div className="modal-header bg-dark text-white border-bottom flex-shrink-0">
              <div>
                <h5 className="modal-title fw-bold mb-0">
                  <i className="fas fa-edit me-2"></i>Edit Partner Application:{" "}
                  {editFormData.restaurant_name || `#${editFormData.id}`}
                </h5>
                <small className="text-white-50">
                  ID #{editFormData.id} &bull; Status: {editFormData.status}
                </small>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => hideModal("editPartnerAppModal")}
                aria-label="Close"
              ></button>
            </div>

            <div
              className="modal-body p-4 flex-grow-1"
              style={{ overflowY: "auto", maxHeight: "calc(90vh - 140px)" }}
            >
              {editLoading && (
                <div className="alert alert-info py-2 mb-3">
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Loading full application details...
                </div>
              )}

              {/* Section 1: Basic & Restaurant Info */}
              <div className="card mb-4 border">
                <div className="card-header bg-light py-2">
                  <h6 className="fw-bold mb-0 text-primary">
                    <i className="fa-solid fa-store me-2"></i>1. Restaurant & Owner Information
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Restaurant Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="restaurant_name"
                        value={editFormData.restaurant_name}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Owner Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="owner_name"
                        value={editFormData.owner_name}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="owner_phone"
                        value={editFormData.owner_phone}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Owner Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="owner_email"
                        value={editFormData.owner_email}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Application Status</label>
                      <select
                        className="form-select"
                        name="status"
                        value={editFormData.status}
                        onChange={handleEditChange}
                      >
                        <option value="draft">Draft</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Address & Location */}
              <div className="card mb-4 border">
                <div className="card-header bg-light py-2">
                  <h6 className="fw-bold mb-0 text-primary">
                    <i className="fa-solid fa-location-dot me-2"></i>2. Address & Location
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">City</label>
                      <input
                        type="text"
                        className="form-control"
                        name="city"
                        value={editFormData.city}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Pincode</label>
                      <input
                        type="text"
                        className="form-control"
                        name="pincode"
                        value={editFormData.pincode}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-semibold">Complete Address</label>
                      <textarea
                        className="form-control"
                        name="complete_address"
                        rows="2"
                        value={editFormData.complete_address}
                        onChange={handleEditChange}
                      ></textarea>
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-semibold">Landmark</label>
                      <input
                        type="text"
                        className="form-control"
                        name="landmark"
                        value={editFormData.landmark}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: Operations & Food Details */}
              <div className="card mb-4 border">
                <div className="card-header bg-light py-2">
                  <h6 className="fw-bold mb-0 text-primary">
                    <i className="fa-solid fa-clock me-2"></i>3. Operations & Setup
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Service Type</label>
                      <select
                        className="form-select"
                        name="service_type"
                        value={editFormData.service_type}
                        onChange={handleEditChange}
                      >
                        <option value="both">Both (Dine-in & Delivery)</option>
                        <option value="delivery">Delivery Only</option>
                        <option value="dinein">Dine-in Only</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Veg Type</label>
                      <select
                        className="form-select"
                        name="veg_type"
                        value={editFormData.veg_type}
                        onChange={handleEditChange}
                      >
                        <option value="pureveg">Pure Veg</option>
                        <option value="both">Both (Veg & Non-Veg)</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Opening Time</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 09:00 AM"
                        name="open_time"
                        value={editFormData.open_time}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Closing Time</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 11:00 PM"
                        name="close_time"
                        value={editFormData.close_time}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Cost for Two (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="cost_for_two"
                        value={editFormData.cost_for_two}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Delivery Radius (km)</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        name="delivery_radius_km"
                        value={editFormData.delivery_radius_km}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Min Order Amount (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="min_order_amount"
                        value={editFormData.min_order_amount}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 4: Legal Details */}
              <div className="card mb-4 border">
                <div className="card-header bg-light py-2">
                  <h6 className="fw-bold mb-0 text-primary">
                    <i className="fa-solid fa-file-shield me-2"></i>4. Legal & Verification
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">FSSAI Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="fssai_no"
                        value={editFormData.fssai_no}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">FSSAI Expiry</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fssai_expiry"
                        value={editFormData.fssai_expiry}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">PAN Card Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="pan_no"
                        value={editFormData.pan_no}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Name on PAN</label>
                      <input
                        type="text"
                        className="form-control"
                        name="pan_name"
                        value={editFormData.pan_name}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">GSTIN</label>
                      <input
                        type="text"
                        className="form-control"
                        name="gst_no"
                        value={editFormData.gst_no}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5: Bank Details */}
              <div className="card border">
                <div className="card-header bg-light py-2">
                  <h6 className="fw-bold mb-0 text-primary">
                    <i className="fa-solid fa-building-columns me-2"></i>5. Bank Details
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Bank Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="bank_name"
                        value={editFormData.bank_name}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Account Holder Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="account_holder_name"
                        value={editFormData.account_holder_name}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Account Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="bank_account_no"
                        value={editFormData.bank_account_no}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">IFSC Code</label>
                      <input
                        type="text"
                        className="form-control"
                        name="ifsc_code"
                        value={editFormData.ifsc_code}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer bg-light flex-shrink-0">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => hideModal("editPartnerAppModal")}
                disabled={editSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={editSubmitting}
              >
                {editSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk me-1"></i> Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerApplications;
